import { useDrop } from 'react-dnd';
import { alpha, Box } from '@mui/material';
import React from 'react';
import useBundleTranslation from 'i18n';
import type { AllowedComponentType } from '@report-content';

export function ContentDropBox({
    isIconDragging,
    blockIndex,
    onAddNewBlock,
    hasSpaceOnTop,
    isSingle,
}: {
    isIconDragging?: boolean;
    blockIndex: number;
    onAddNewBlock: (component: AllowedComponentType, index: number) => void;
    hasSpaceOnTop?: boolean;
    isSingle?: boolean;
}) {
    const { t } = useBundleTranslation(['components/report-content']);
    const [{ isOver }, drop] = useDrop(
        () => ({
            accept: 'component',
            canDrop: () => true,
            drop: (obj: AllowedComponentType) => {
                onAddNewBlock(obj, blockIndex);
            },
            collect: (monitor) => ({
                isOver: !!monitor.isOver(),
                canDrop: !!monitor.canDrop(),
            }),
        }),
        [blockIndex],
    );

    if (isSingle) {
        return (
            <Box
                ref={drop}
                sx={{
                    width: '100%',
                    height: '50px',
                    border: '1px dashed',
                    backgroundColor: isOver ? (theme) => alpha(theme.palette.primary.main, 0.04) : undefined,
                    borderColor: (theme) =>
                        isOver ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.24),
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: (theme) => theme.palette.primary.main,
                    textAlign: 'center',
                    position: 'relative',
                }}
                data-test={'drop-components-area'}
            >
                {t('drop_component_here')}
            </Box>
        );
    }

    return (
        <Box
            ref={drop}
            className={`indent-and-drop-area ${isIconDragging ? 'drop-area-active' : ''} ${blockIndex == 0 && !hasSpaceOnTop ? 'first-special' : ''} ${isOver ? 'drop-area-over' : ''}`}
            sx={{
                width: '100%',
                height: '16px',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                '&.drop-area-active:before': {
                    content: "''",
                    width: '100%',
                    height: '100%',
                    py: 2,
                    top: '50%',
                    position: 'absolute',
                    boxSizing: 'content-box',
                    transform: 'translateY(-50%)',
                },
                '&.drop-area-active:after': {
                    position: 'absolute',
                    content: "''",
                    width: '100%',
                    height: '100%',
                    border: '1px dashed',
                    borderColor: (theme) => alpha(theme.palette.text.primary, 0.24),
                    borderRadius: 1,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.04),
                },
                '&.drop-area-over:after': {
                    color: (theme) => alpha(theme.palette.primary.main, 1),
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                    borderColor: (theme) => alpha(theme.palette.primary.main, 1),
                },
                '&.first-special': {
                    height: 0,
                    '&.drop-area-active:after': {
                        height: '16px',
                        transform: 'translateY(-100%)',
                    },
                },
            }}
            data-test={'drop-components-area'}
        />
    );
}
